import { marketingParseJson } from './marketingModel';
import {
  IMarketingOfflineConversions,
  IMarketingOfflineCount,
  MarketingDate,
  MarketingJson,
} from './types';

/**
 * ТЗ 0091 — «Настройки → Офлайн-конверсии»: чистые помощники экрана журнала
 * выгрузки продаж ERP в Google Ads и Meta. Без React, Apollo и i18n (подписи —
 * ключами `marketing.settings.offline.*`), как и `marketingModel`.
 *
 * Коды (площадка, событие, статус, причина) сервер отдаёт строками, экран их
 * переводит. Незнакомый код не пропадает: площадка и событие — как пришли,
 * причина — кодом моноширинным (`meta_190_0`, коды Google).
 */

/** Площадки выгрузки (`OfflinePlatform`). */
export const MARKETING_OFFLINE_PLATFORMS = ['google', 'meta'] as const;

/** События: продажа и раннее событие Meta «договор отправлен». */
export const MARKETING_OFFLINE_EVENTS = ['sale', 'contract_sent'] as const;

/** Статусы строки журнала (`OfflineConversionUpload.status`). */
export const MARKETING_OFFLINE_STATUSES = [
  'pending',
  'sent',
  'rejected',
  'skipped_no_consent',
  'skipped_window',
  'skipped_before_launch',
  'retract_pending',
  'retracted',
] as const;

export type MarketingOfflineStatus = typeof MARKETING_OFFLINE_STATUSES[number];

/**
 * Причины, которые пишет сам сервер (а не площадка): нет ключей совпадения,
 * отзыв выключен флагом, у Meta отзыва нет, отправка со стенда запрещена.
 * Прочие коды — площадки, показываются как есть.
 */
export const MARKETING_OFFLINE_ERROR_CODES = [
  'no_match_keys',
  'retract_off',
  'journal_only',
  'stand_guard',
  // Продажа снова действует, а в Google уже отозвана — добавить руками.
  'reinstated_after_retract',
  // Meta уже получила эту покупку по дублю документа PandaDoc — не шлём.
  'superseded_duplicate',
] as const;

/** Причина, после которой дело за человеком: строка подсвечена. */
const ACTION_ERROR_CODES: ReadonlyArray<string> = ['reinstated_after_retract'];

/** Источники документа строки журнала (`source`). */
export const MARKETING_OFFLINE_DOCUMENT_SOURCES = [
  'esign',
  'pandadoc',
] as const;

/** Статус прогона (`MarketingSyncRun`, источник `offline_conversions`). */
export const MARKETING_OFFLINE_RUN_STATUSES = [
  'running',
  'success',
  'failed',
] as const;

/** Сколько пропусков и отказов просить у сервера (он отдаёт не больше 200). */
export const MARKETING_OFFLINE_ISSUES_LIMIT = 50;

/**
 * Группы счётчиков: отправлено, ждёт отправки, пропущено (без согласия,
 * вне окна, до даты запуска), отклонено площадкой, отмены продаж (ждёт
 * отзыва и отозвано). `other` — статус, которого экран не знает: его строки
 * не пропадают из счёта, а плитка появляется, только если они есть.
 */
export const MARKETING_OFFLINE_COUNT_GROUPS = [
  'sent',
  'pending',
  'skipped',
  'rejected',
  'retract',
  'other',
] as const;

export type MarketingOfflineCountGroup = typeof MARKETING_OFFLINE_COUNT_GROUPS[number];

const GROUP_OF: Readonly<
  Record<MarketingOfflineStatus, MarketingOfflineCountGroup>
> = {
  pending: 'pending',
  sent: 'sent',
  rejected: 'rejected',
  skipped_no_consent: 'skipped',
  skipped_window: 'skipped',
  skipped_before_launch: 'skipped',
  retract_pending: 'retract',
  retracted: 'retract',
};

const isStatus = (value: string): value is MarketingOfflineStatus =>
  (MARKETING_OFFLINE_STATUSES as readonly string[]).includes(value);

/** Группа счётчика по статусу; незнакомый — `other`. */
export const marketingOfflineCountGroup = (
  status: string,
): MarketingOfflineCountGroup =>
  isStatus(status) ? GROUP_OF[status] : 'other';

const keyIn = (
  prefix: string,
  list: ReadonlyArray<string>,
  code: string | null | undefined,
): string | null =>
  code && list.includes(code)
    ? `marketing.settings.offline.${prefix}.${code}`
    : null;

/** Подпись площадки; незнакомая — `null` (экран покажет код). */
export const marketingOfflinePlatformKey = (code: string | null | undefined) =>
  keyIn('platforms', MARKETING_OFFLINE_PLATFORMS, code);

export const marketingOfflineEventKey = (code: string | null | undefined) =>
  keyIn('events', MARKETING_OFFLINE_EVENTS, code);

export const marketingOfflineStatusKey = (code: string | null | undefined) =>
  keyIn('statuses', MARKETING_OFFLINE_STATUSES, code);

/** Что значит статус словами — причина строки, когда кода нет. */
export const marketingOfflineStatusHintKey = (
  code: string | null | undefined,
) => keyIn('statusHints', MARKETING_OFFLINE_STATUSES, code);

/** Своя причина сервера; код площадки — `null` (показывается как есть). */
export const marketingOfflineErrorKey = (code: string | null | undefined) =>
  keyIn('errors', MARKETING_OFFLINE_ERROR_CODES, code);

export const marketingOfflineRunStatusKey = (code: string | null | undefined) =>
  keyIn('runStatuses', MARKETING_OFFLINE_RUN_STATUSES, code);

export type MarketingOfflineTone =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'info'
  | 'neutral';

/**
 * Тон статуса строки: отправлено — зелёный, ждёт — синий, отклонено —
 * красный, ждёт отзыва — жёлтый (руками или флагом), пропуски и отозванное —
 * серый.
 */
export const marketingOfflineStatusTone = (
  status: string | null | undefined,
): MarketingOfflineTone => {
  switch (status) {
    case 'sent':
      return 'ok';
    case 'pending':
      return 'info';
    case 'rejected':
      return 'danger';
    case 'retract_pending':
      return 'warn';
    default:
      return 'neutral';
  }
};

/**
 * Тон строки журнала: как у статуса, но причина, после которой дело за
 * человеком (продажа вернулась после отзыва в Google), — жёлтым при любом
 * статусе.
 */
export const marketingOfflineIssueTone = (issue: {
  status: string;
  errorCode?: string | null;
}): MarketingOfflineTone =>
  issue.errorCode && ACTION_ERROR_CODES.includes(issue.errorCode)
    ? 'warn'
    : marketingOfflineStatusTone(issue.status);

/**
 * Подпись документа строки журнала — ключом и подстановками:
 * «Договор ERP #123» / «Umowa ERP #123», «PandaDoc <id>»; незнакомый
 * источник — «<источник> <номер>». Та же подпись — в шапке панели продажи.
 */
export const marketingOfflineDocumentText = (issue: {
  source: string;
  sourceId: string;
}): { key: string; params: Record<string, string> } =>
  (MARKETING_OFFLINE_DOCUMENT_SOURCES as readonly string[]).includes(
    issue.source,
  )
    ? {
        key: `marketing.settings.offline.document.${issue.source}`,
        params: { id: issue.sourceId },
      }
    : {
        key: 'marketing.settings.offline.document.other',
        params: { source: issue.source, id: issue.sourceId },
      };

/** Тон прогона — как у синхронизации Kommo (`kommoSyncStatusTone`). */
export const marketingOfflineRunTone = (
  status: string | null | undefined,
): MarketingOfflineTone => {
  switch (status) {
    case 'success':
      return 'ok';
    case 'failed':
      return 'danger';
    case 'running':
      return 'info';
    default:
      return 'neutral';
  }
};

/**
 * Тон плитки: красным — только ненулевые отказы, жёлтым — ненулевые отмены
 * продаж («0 проблем» красным пугает зря).
 */
export const marketingOfflineGroupTone = (
  group: MarketingOfflineCountGroup,
  value: number,
): 'plain' | 'warn' | 'danger' => {
  if (value <= 0) return 'plain';
  if (group === 'rejected') return 'danger';
  if (group === 'retract' || group === 'other') return 'warn';
  return 'plain';
};

/* ── Счётчики ────────────────────────────────────────────────────────── */

export type MarketingOfflineGroupCounts = Readonly<
  Record<MarketingOfflineCountGroup, number>
>;

/** Поток выгрузки: площадка × событие, счётчики за 7 и 30 дней. */
export interface IMarketingOfflineStream {
  platform: string;
  event: string;
  week: MarketingOfflineGroupCounts;
  month: MarketingOfflineGroupCounts;
}

/**
 * Потоки, которые показываются всегда (нули — тоже ответ): продажи в Google,
 * продажи в Meta и «договор отправлен» в Meta. У Google раннего события нет.
 */
export const MARKETING_OFFLINE_STREAMS: ReadonlyArray<{
  platform: string;
  event: string;
}> = [
  { platform: 'google', event: 'sale' },
  { platform: 'meta', event: 'sale' },
  { platform: 'meta', event: 'contract_sent' },
];

const emptyCounts = (): Record<MarketingOfflineCountGroup, number> => ({
  sent: 0,
  pending: 0,
  skipped: 0,
  rejected: 0,
  retract: 0,
  other: 0,
});

const streamKey = (platform: string, event: string) => `${platform}:${event}`;

/**
 * Счётчики журнала по потокам: строки «площадка × событие × статус» за 7 и
 * 30 дней сложены в группы. Известные потоки — в своём порядке, незнакомая
 * пара из ответа — следом (её строки не должны пропасть из счёта).
 */
export const marketingOfflineStreams = (
  counts7d: ReadonlyArray<IMarketingOfflineCount>,
  counts30d: ReadonlyArray<IMarketingOfflineCount>,
): IMarketingOfflineStream[] => {
  const order = MARKETING_OFFLINE_STREAMS.map(({ platform, event }) =>
    streamKey(platform, event),
  );
  const streams = new Map<
    string,
    {
      platform: string;
      event: string;
      week: Record<MarketingOfflineCountGroup, number>;
      month: Record<MarketingOfflineCountGroup, number>;
    }
  >();
  const streamOf = (platform: string, event: string) => {
    const key = streamKey(platform, event);
    let stream = streams.get(key);
    if (!stream) {
      stream = { platform, event, week: emptyCounts(), month: emptyCounts() };
      streams.set(key, stream);
      if (!order.includes(key)) order.push(key);
    }
    return stream;
  };
  MARKETING_OFFLINE_STREAMS.forEach(({ platform, event }) =>
    streamOf(platform, event),
  );
  const add = (
    rows: ReadonlyArray<IMarketingOfflineCount>,
    period: 'week' | 'month',
  ) =>
    rows.forEach((row) => {
      const stream = streamOf(row.platform, row.event);
      stream[period][marketingOfflineCountGroup(row.status)] += row.count;
    });
  add(counts7d, 'week');
  add(counts30d, 'month');
  return order
    .map((key) => streams.get(key))
    .filter((stream): stream is IMarketingOfflineStream => Boolean(stream));
};

/** Группы плиток потока: `other` — только если в нём что-то есть. */
export const marketingOfflineVisibleGroups = (
  stream: IMarketingOfflineStream,
): MarketingOfflineCountGroup[] =>
  MARKETING_OFFLINE_COUNT_GROUPS.filter(
    (group) =>
      group !== 'other' || stream.week.other > 0 || stream.month.other > 0,
  );

/* ── Флаги ───────────────────────────────────────────────────────────── */

/**
 * Почему площадкам сейчас ничего не уходит (пусто — уходит):
 *  - `disabled` — главный флаг выгрузки выключен;
 *  - `noPlatform` — главный включён, но ни одна площадка;
 *  - `noLaunch` — дата запуска не задана (без неё не уходит ничего).
 * Флаги и дату задаёт администратор на сервере — в ERP они только видны.
 */
export type MarketingOfflineIdleReason = 'disabled' | 'noPlatform' | 'noLaunch';

export const MARKETING_OFFLINE_IDLE_REASONS: ReadonlyArray<MarketingOfflineIdleReason> = [
  'disabled',
  'noPlatform',
  'noLaunch',
];

export const marketingOfflineIdleReasons = (
  data: Pick<
    IMarketingOfflineConversions,
    'enabled' | 'googleEnabled' | 'metaEnabled' | 'since'
  >,
): MarketingOfflineIdleReason[] => {
  const reasons: MarketingOfflineIdleReason[] = [];
  if (!data.enabled) reasons.push('disabled');
  else if (!data.googleEnabled && !data.metaEnabled) {
    reasons.push('noPlatform');
  }
  if (!data.since) reasons.push('noLaunch');
  return reasons;
};

/** Включена ли площадка (флаг сервера уже учитывает главный). */
export const marketingOfflinePlatformEnabled = (
  data: Pick<IMarketingOfflineConversions, 'googleEnabled' | 'metaEnabled'>,
  platform: string,
): boolean | null => {
  if (platform === 'google') return data.googleEnabled;
  if (platform === 'meta') return data.metaEnabled;
  return null;
};

/* ── Прогон ──────────────────────────────────────────────────────────── */

const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Окно продаж прогона из его итога (`stats.window` — `[с, по]`); нет или
 * мусор — `null`.
 */
export const marketingOfflineRunWindow = (
  stats: MarketingJson | string | null | undefined,
): { from: MarketingDate; to: MarketingDate } | null => {
  const parsed = marketingParseJson(stats ?? {});
  const { window } = parsed;
  if (!Array.isArray(window) || window.length !== 2) return null;
  const [from, to] = window;
  if (typeof from !== 'string' || typeof to !== 'string') return null;
  if (!ISO_DAY_RE.test(from) || !ISO_DAY_RE.test(to)) return null;
  return { from, to };
};
